// components/DynamicTable.jsx
import React, { useState, useEffect } from 'react';
import { AVAILABLE_COLUMNS, ROLE_DEFAULT_COLUMNS, DEFAULT_COLUMNS_TABLA, COLUMN_GROUP_CLASSES, sortColumnsByOrder, insertAtCanonicalSpot } from '../datos/configColumns';
import ColumnSelector from './ColumnSelector';
import { LoadingModal } from './ui';

// Barra de avance individual (formato de la tabla original)
const AvanceBar = ({ value, barColor = '', label }) => (
  <div className="flex items-center space-x-2">
    <div className="flex-1 rounded-full overflow-hidden h-2 bg-gray-200 dark:bg-gray-700 min-w-[60px]">
      <div
        className={`h-2 ${value >= 100 ? 'bg-green-600' : value > 0 ? (barColor || 'bg-indigo-600') : ''}`}
        style={{ width: `${Math.min(value || 0, 100)}%` }}
      ></div>
    </div>
    <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
      {label ? `${label} ` : ''}{Math.round(value || 0)}%
    </span>
  </div>
);

// Avance Financiero compuesto: barra de Convenio + barras de cada Adenda (como la tabla original)
const AvanceFinancieroCell = ({ item }) => (
  <div className="w-40 space-y-1">
    <AvanceBar value={item.detalleConvenio || 0} />
    {item.tieneAdenda1 && (
      <div className="flex items-center space-x-2">
        <div className="flex-1 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 h-2 min-w-[60px]">
          <div
            className={`h-2 ${item.detalleAdenda1 >= 100 ? 'bg-green-600' : item.detalleAdenda1 > 0 ? 'bg-purple-500' : ''}`}
            style={{ width: `${Math.min(item.detalleAdenda1, 100)}%` }}
          ></div>
        </div>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap w-7 text-right">A1 {Math.round(item.detalleAdenda1)}%</span>
      </div>
    )}
    {item.tieneAdenda2 && (
      <div className="flex items-center space-x-2">
        <div className="flex-1 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 h-2 min-w-[60px]">
          <div
            className={`h-2 ${item.detalleAdenda2 >= 100 ? 'bg-green-600' : item.detalleAdenda2 > 0 ? 'bg-pink-500' : ''}`}
            style={{ width: `${Math.min(item.detalleAdenda2, 100)}%` }}
          ></div>
        </div>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap w-7 text-right">A2 {Math.round(item.detalleAdenda2)}%</span>
      </div>
    )}
  </div>
);

// Renderizador de celdas según tipo de columna
const CellRenderer = ({ column, value, item }) => {
  // Avance Financiero compuesto (Convenio + Adendas)
  if (column.composite) {
    return <AvanceFinancieroCell item={item} />;
  }

  // Columnas con barra de progreso
  if (column.id === 'avanceFisico' || column.id === 'ultimoPorcentajeObra') {
    return (
      <div className="w-40 space-y-1">
        <AvanceBar value={value || 0} />
      </div>
    );
  }

  // Columnas de montos
  if (column.id === 'monto_obra' || column.id === 'montoAdenda1' || column.id === 'montoAdenda2' || column.id === 'montoTotal') {
    return <span>{value || 'N/A'}</span>;
  }

  // Columnas de observaciones (texto largo). Son cuatro campos distintos: el de seguimiento
  // de obra y los tres de la pestaña Tramitación (externa, reporte a la entidad e internas),
  // que antes se confundían porque todos se llamaban "Observaciones".
  const OBSERVACIONES_IDS = ['observaciones', 'observaciones_externas', 'observaciones_reporte', 'observaciones_internas'];
  if (OBSERVACIONES_IDS.includes(column.id)) {
    return (
      <div className="max-w-xs truncate" title={value}>
        {value || 'N/A'}
      </div>
    );
  }

  // Columna de beneficiarios (puede ser string o array)
  if (column.id === 'beneficiarios') {
    const beneficiaries = Array.isArray(value) ? value.join(', ') : value;
    return <div className="max-w-xs truncate" title={beneficiaries}>{beneficiaries || 'N/A'}</div>;
  }

  // Texto largo: nombre de obra y organismos (formato de la tabla original)
  if (column.id === 'nombreObra' || column.id === 'organismo_solicitante' || column.id === 'modalidad') {
    return (
      <div className="whitespace-normal" title={value}>
        {value}
      </div>
    );
  }

  // Valores por defecto
  return <span>{value || 'N/A'}</span>;
};

// Buscar una columna por su key o id real (la key puede diferir del id, ej. avanceFinanciero → detalleConvenio)
const findColumnById = (id) => AVAILABLE_COLUMNS[id] || Object.values(AVAILABLE_COLUMNS).find(c => c.id === id) || null;

// Columnas visibles por defecto: las 19 que veía admin, iguales para TODOS los roles.
const DEFAULT_COLUMN_IDS = DEFAULT_COLUMNS_TABLA;
const DEFAULT_COLUMNS = DEFAULT_COLUMN_IDS.map(findColumnById).filter(Boolean);
// Columnas siempre presentes: se mantienen las 15 históricas de admin (no se pueden
// ocultar). Las 4 que quedan en el default pero no acá (Modalidad, Exp. Adenda 1,
// Exp. Adenda 2 y Último Pago) se ven por defecto y el usuario las puede apagar.
const ALWAYS_VISIBLE_IDS = new Set(
  ROLE_DEFAULT_COLUMNS.admin.columns.flatMap(k => [k, findColumnById(k)?.id]).filter(Boolean)
);
const ALWAYS_VISIBLE_COLUMNS = ROLE_DEFAULT_COLUMNS.admin.columns.map(findColumnById).filter(Boolean);

const DynamicTable = ({ data, onSaveColumnPreferences, visibleColumns: controlledVisibleColumns, onColumnsChange, isLoading = false }) => {
  const [internalVisibleColumns, setInternalVisibleColumns] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const isControlled = controlledVisibleColumns !== undefined;
  const visibleColumns = isControlled ? controlledVisibleColumns : internalVisibleColumns;

  const setVisible = (cols) => {
    if (isControlled) {
      onColumnsChange && onColumnsChange(cols);
    } else {
      setInternalVisibleColumns(cols);
    }
  };

  // Clave única de preferencias (no varía por perfil: todos comparten las mismas columnas).
  // v3: el default pasó de 15 (admin.columns) a 19 columnas iguales para todos; un storage
  // v2 con 15 no se puede distinguir de una personalización, así que se descarta una vez.
  const STORAGE_KEY = 'table_columns_personalizada_v3';

  const loadColumns = () => {
    const savedColumns = localStorage.getItem(STORAGE_KEY);

    if (!savedColumns) return sortColumnsByOrder(DEFAULT_COLUMNS);

    // Se restaura el orden guardado tal cual, porque el usuario puede haber arrastrado
    // columnas. Antes se armaba "las 18 primero + las demás anexadas", que destruía
    // cualquier orden custom.
    const restored = JSON.parse(savedColumns).map(findColumnById).filter(Boolean);

    // Las siempre visibles que no estén en las preferencias (columna nueva agregada al
    // código después de que el usuario guardara) se agregan al final.
    const missing = ALWAYS_VISIBLE_COLUMNS.filter(col => !restored.some(r => r.id === col.id));
    return [...restored, ...missing];
  };

  // Cargar columnas visibles desde localStorage o usar default (informe original)
  useEffect(() => {
    const columns = loadColumns();
    if (isControlled) {
      onColumnsChange && onColumnsChange(columns);
    } else {
      setInternalVisibleColumns(columns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guardar preferencias cuando cambian las columnas
  useEffect(() => {
    if (visibleColumns.length > 0) {
      const columnIds = visibleColumns.map(col => col.id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(columnIds));

      if (onSaveColumnPreferences) {
        onSaveColumnPreferences(columnIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns]);

// Manejar toggle de columnas
  const handleToggleColumn = (columnId) => {
    const column = findColumnById(columnId);
    if (!column || column.required || ALWAYS_VISIBLE_IDS.has(column.id)) return; // No permitir ocultar columnas siempre presentes

    const exists = visibleColumns.some(col => col.id === columnId);
    // Al ocultar solo se saca. Al mostrar entra en su lugar del orden inicial, pero sin
    // tocar el orden de las que ya están visibles (que puede ser uno arrastrado a mano).
    setVisible(exists
      ? visibleColumns.filter(col => col.id !== columnId)
      : insertAtCanonicalSpot(visibleColumns, column));
  };

  // Reordenar: vuelve al orden inicial conservando qué columnas están visibles
  const handleReorder = () => {
    setVisible(sortColumnsByOrder(visibleColumns));
  };

  // Seleccionar todas las columnas (manteniendo siempre presentes las del default)
  const handleSelectAll = () => {
    setVisible(sortColumnsByOrder(Object.values(AVAILABLE_COLUMNS)));
  };

  // Selección inicial: vuelve a las 19 columnas por defecto, en su orden
  const handleInitialSelection = () => {
    setVisible(sortColumnsByOrder(DEFAULT_COLUMNS));
  };

  // --- Arrastrar columnas para reordenar (HTML5 nativo, sin dependencias) ---
  const handleDragStart = (index) => setDragIndex(index);

  const handleDragOver = (event, index) => {
    event.preventDefault();
    if (overIndex !== index) setOverIndex(index);
  };

  const handleDrop = (event, index) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = [...visibleColumns];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    setVisible(next);
    setDragIndex(null);
    setOverIndex(null);
  };

  const endDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const thClasses = `px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider`;
  const tdClasses = `px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200`;

  return (
    <div className="space-y-4">
      {/* Barra de herramientas */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <ColumnSelector
            availableColumns={AVAILABLE_COLUMNS}
            visibleColumns={visibleColumns}
            onToggleColumn={handleToggleColumn}
            onReorder={handleReorder}
            onSelectAll={handleSelectAll}
            onInitialSelection={handleInitialSelection}
            alwaysVisibleIds={ALWAYS_VISIBLE_IDS}
          />
        </div>
        
        <div className="text-sm text-gray-500">
          {visibleColumns.length} columnas visibles
        </div>
      </div>
      
      {/* Tabla */}
      <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              {visibleColumns.map((column, colIndex) => (
                <th
                  key={column.id}
                  draggable
                  onDragStart={() => handleDragStart(colIndex)}
                  onDragOver={(e) => handleDragOver(e, colIndex)}
                  onDrop={(e) => handleDrop(e, colIndex)}
                  onDragEnd={endDrag}
                  title="Arrastrá para reordenar"
                  className={`${thClasses} ${COLUMN_GROUP_CLASSES[column.group] || ''} cursor-grab active:cursor-grabbing select-none ${dragIndex === colIndex ? 'opacity-40' : ''} ${overIndex === colIndex && dragIndex !== null && dragIndex !== colIndex ? 'ring-2 ring-inset ring-blue-500' : ''}`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((item, index) => (
              <tr key={item.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                {visibleColumns.map(column => (
                  <td key={column.id} className={tdClasses}>
                    <CellRenderer 
                      column={column} 
                      value={item[column.id]} 
                      item={item}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        
        {!isLoading && data.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No hay datos para mostrar
          </div>
        )}

        {/* Indicador de carga: el MISMO LoadingModal que usa la tabla fija (fixed inset-0
            z-50), no un texto abajo de la tabla. Antes de esto el overlay venia de
            TablaInformes.jsx, que para admin se renderizaba arriba de esta tabla; al sacar
            la Tabla Original se fue el indicador y solo quedaba este texto, que encima
            aparece debajo de una tabla vacia. Se deja como overlay y no como return
            temprano para que la tabla y el selector sigan montados debajo (las
            preferencias de columnas se leen de localStorage al montar). */}
        {isLoading && <LoadingModal texto="Cargando informes..." />}
      </div>
    </div>
  );
};

export default DynamicTable;
